<script setup>
import { defineProps } from 'vue';
import DropdownMenuGroup from './../../pge-components/pge-dropdown/pge-dropdown-group.vue';
import DropdownMenu from './../../pge-components/pge-dropdown/pge-dropdown.vue';
import PreviewTemplate from './Template.vue';

const { logo, eventName, groups, countries, regions, policyLines } = defineProps({
  logo: {
    type: String,
    required: true
  },
  eventName: {
    type: String,
    default: ''
  },
  groups: {
    type: Array,
  },
  countries: {
    type: Array,
  },
  regions: {
    type: Array,
  },
  // The Policy type field's choices, so the preview shows the question the way
  // a registrant will actually meet it rather than as an empty dropdown.
  policyLines: {
    type: Array,
    default: () => [],
  }
});
</script>

<template>
  <preview-template :logo="logo" :header-text="eventName ? `${eventName} Registration` : 'Registration'">
    <template #header-content>
      <h2>Registration Form Preview</h2>
      <p>This is what invitees will see when registering for your hub.</p>
    </template>

    <template #form-content>
      <p class="form-note">(Email and First and Last Name are collected in a previous step)</p>
      <template v-for="(group, outerIndex) in groups" :key="outerIndex">
        <div class="form-title">
          {{ group.title ? group.title : 'Section Name' }}
        </div>
        <div v-for="(field, innerIndex) in group.fields" :key="innerIndex" class="form-group">
          <div v-if="field.type === 'text'" class="form-input">
            <span class='separated form-input__label'>
              {{ field.title ? field.title.replace(/\s+/g, '') === 'State/Region/Province' ? 'State / Province' :
                field.title : 'Field Name' }}
              <span v-if="field.required">*</span>
            </span>
            <div></div>
          </div>
          <div class="form-group__pge-dropdown" v-if="field.type === 'country'">
            <dropdown-menu 
              :label="field.title"
              labelType="separated" 
              :name="field.name"
              :options="countries.map(item => ({ label: item, value: item }))" 
              :required="field.required"
              :has-required-sign="field.required" 
              :model-value="field.value"
              v-model="field.value"
            />
          </div>
          <div class="form-group__pge-dropdown" v-if="field.type === 'state'">
            <dropdown-menu-group 
              :required="field.required" 
              :has-required-sign="field.required" 
              :label="field.title"
              labelType="separated" 
              :name="field.name" 
              :options="Object.entries(regions).reduce((acc, [country, items]) => {
                acc[country] = items.map(item => ({ label: item, value: item }));
                return acc;
              }, {})"
              :model-value="field.value"
              v-model="field.value"
            />
          </div>
          <div class="form-group__pge-dropdown" v-if="field.type === 'policy_line'">
            <dropdown-menu
              :model-value="field.value"
              v-model="field.value"
              :label="field.title"
              labelType="separated"
              :name="field.name"
              :required="field.required"
              :has-required-sign="field.required"
              :options="policyLines.filter(line => !line.archived).map(line => ({ label: line.name, value: String(line.id) }))"
            />
          </div>
          <div class="form-group__pge-dropdown" v-if="field.type === 'custom'">
            <dropdown-menu 
              :model-value="field.value"
              v-model="field.value"
              :label="field.title" 
              labelType="separated" 
              :name="field.name" 
              :required="field.required"
              :has-required-sign="field.required"
              :options="field.options.split(',').map(item => ({ label: item, value: item }))" />
          </div>
        </div>
      </template>
      <div class="form-actions">
        <div class="form-actions__close">Close</div>
        <div class="form-actions__submit">Submit</div>
      </div>
    </template>
  </preview-template>
</template>

<style lang="scss" scoped>
input {
  border-radius: 5px;
  border: 1px solid #9B9B9B;
}

.form-group {
  display: flex;
  column-gap: 17px;
  margin-bottom: 26px;

  &__pge-dropdown {
    width: 100%;
    position: relative;
    background-color: white;
  }
}

.form-input {
  position: relative;
  width: 100%;

  &__label {
    background-color: #fff;
    color: #000;
    position: absolute;
    top: -10px;
    left: 15px;
    display: inline-block;
    width: auto;
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 20px;
    padding: 0 5px;
    z-index: 50;
    cursor: default;

    span {
      color: #ff0000;
    }

    &.separated {
      position: static;
      padding: 0;
      margin-bottom: 16px;
      color: #49454F;
      font-feature-settings: 'liga' off, 'clig' off;
      font-family: Lato, sans-serif;
      font-size: 16px;
      line-height: 24px;
    }
  }

  div {
    border: 1px solid #9B9B9B;
    border-radius: 5px;
    width: 100%;
    padding: 24px;
  }

  .select {
    position: relative;

    .expand {
      right: 10px;
      width: 10px;
      height: 10px;
    }
  }

  .select>* {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
}

.form-title {
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 19px;
  color: #000000;
  margin: 15px 0 26px;
}

.form-note {
  font-style: italic;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: #000000;
  margin: 15px 0 26px;
}

.form-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 15px;

  div {
    display: block;
    border-radius: 100px;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;
    text-align: center;
    margin: 0;
    padding: 10px 24px 10px 24px;
  }

  &__close {
    color: #5E2F78;
  }

  &__submit {
    background-color: #583174;
    color: #fff;
  }
}
</style>
